/**
 * @description HeatmapPeriodSection component
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module components/HeatmapPeriodSection
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * @description The HeatmapPeriodSection component.
 * @class 
 * @extends {React.Component}
 */
class HeatmapPeriodSection extends React.Component {

    /**
     * @inheritdoc
     */
    constructor(props) {
        super(props);

        this.state = {
            showTooltip: false,
            fadeIn: false
        };
    }

    /**
     * @inheritdoc
     */
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.showTooltip !== nextState.showTooltip ||
            this.state.fadeIn !== nextState.fadeIn ||
            this.props.link !== nextProps.link
        );
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        // Timeout allows fadeIn transition to work correctly
        this.timeout = setTimeout(
            () =>
                this.setState({
                    fadeIn: true
                }),
            0
        );
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        document.removeEventListener('click', this.documentClickCallback);
        clearTimeout(this.timeout);
    }

    /**
     * @inheritdoc
     */
    render() {
        const transitionEnterTimeout = 1500 * (this.props.index / this.props.count),
            sectionStyle = {
                transitionDelay: this.props.isLatest
                    ? `${transitionEnterTimeout}ms`
                    : 0
            },
            classNames = `HeatmapPeriodSection-button delay-colour-${this.props.link.delayBand && this.props.link.delayBand.id ? this.props.link.delayBand.id : 0}${
                this.state.fadeIn ? ' fadeIn-visible' : ''
            }${this.props.isLatest ? ' fadeIn' : ''}${
                this.state.showTooltip ? ' active' : ''
            }`;

        return (
            <button
                type="button"
                className={classNames}
                style={sectionStyle}
                onClick={this.handleButtonClick.bind(this)}
                aria-label={`${this.props.link.name} = ${this.props.link.delayBand.description}`}></button>
        );
    }

    /**
     * @description Sets the tooltip state.
     * @param {bool} newState - The state of the tooltip
     */
    setTooltipState(newState) {
        this.setState({
            showTooltip: newState
        });
    }

    /**
     * @description Handles the clicking of the button.
     * @param {Object} event - The click event.
     */
    handleButtonClick(event) {
        event.persist();
        this.setTooltipState(true);
        this.props.openTooltip(
            event,
            this.props.link,
            this.setTooltipState.bind(this)
        );
    }
}

HeatmapPeriodSection.defaultProps = {
    index: 0,
    count: 0,
    link: {},
    isLatest: false,
    openTooltip: () => {}
};

HeatmapPeriodSection.propTypes = {
    index: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    link: PropTypes.object.isRequired,
    isLatest: PropTypes.bool.isRequired,
    openTooltip: PropTypes.func.isRequired
};

export default HeatmapPeriodSection;
